
import * as React from "react"
import DOMPurify from 'dompurify';
import { cn } from "@/lib/utils"
import { detectSecurityThreats } from "@/utils/securityUtils";

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  formatPrice?: boolean;
  preventXss?: boolean;
  detectThreats?: boolean;
  onSecurityThreat?: (threat: { isThreat: boolean; reason?: string }) => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, formatPrice, preventXss = true, detectThreats = true, onChange, onSecurityThreat, ...props }, ref) => {
    // Handle price input formatting
    const handlePriceInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      
      // Remove non-numeric characters except decimal point
      let cleaned = value.replace(/[^\d.]/g, '');
      
      // Ensure only one decimal point
      const decimalPoints = cleaned.match(/\./g);
      if (decimalPoints && decimalPoints.length > 1) {
        cleaned = cleaned.replace(/\.(?=.*\.)/g, '');
      }
      
      // Split by decimal point
      const parts = cleaned.split('.');
      
      // Limit whole number part to 5 digits (max $99999)
      if (parts[0] && parts[0].length > 5) {
        parts[0] = parts[0].substring(0, 5);
      }
      
      // Limit decimal part to 2 digits
      if (parts[1] && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
      }
      
      // Check if the value exceeds 99999.99
      const numValue = parseFloat(parts.join('.'));
      if (numValue > 99999.99) {
        parts[0] = "99999";
        parts[1] = "99";
      }
      
      // Reconstruct the value
      cleaned = parts.join('.');
      
      // Update the input value
      e.target.value = cleaned;
    };

    // Handle input with enhanced security
    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      const originalValue = e.target.value;
      
      // Check for security threats if enabled
      if (detectThreats) {
        const threatCheck = detectSecurityThreats(originalValue);
        if (threatCheck.isThreat && onSecurityThreat) {
          onSecurityThreat(threatCheck);
        }
      }
      
      if (preventXss) {
        // Apply strict sanitization to prevent XSS
        const sanitizedValue = DOMPurify.sanitize(originalValue, {
          ALLOWED_TAGS: [],  // No HTML tags allowed
          ALLOWED_ATTR: [],  // No attributes allowed
          KEEP_CONTENT: true, // Keep the text content
          SANITIZE_DOM: true // Sanitize DOM
        });
        
        // Update input value only if it changed after sanitization
        if (sanitizedValue !== originalValue) {
          e.target.value = sanitizedValue;
        }
      }
      
      // Call the original onChange handler if provided
      if (onChange) {
        onChange(e);
      }
    };

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          className
        )}
        ref={ref}
        onInput={formatPrice ? handlePriceInput : handleInput}
        // Add additional security attributes
        autoComplete={type === "password" ? "new-password" : props.autoComplete}
        spellCheck={type === "password" ? "false" : props.spellCheck}
        autoCorrect={type === "password" ? "off" : props.autoCorrect as any}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
